import { Command } from "commander";
import chalk from "chalk";
import { createSnapshot, listSnapshots, restoreSnapshot } from "../snapshot/snapshotEngine.js";
import { findPackageJson } from "../utils/files.js";
import path from "path";
import fs from "fs";

// ── snapshot create ────────────────────────────────────────────────────────

const snapshotCreateCommand = new Command("create")
  .description("Create a project snapshot capturing current dependency state")
  .option("--label <label>", "Human-readable label for this snapshot")
  .option("--json", "Output in JSON format")
  .action(async (options: { label?: string; json?: boolean }) => {
    const cwd = process.cwd();
    try {
      const snapshot = await createSnapshot(cwd, options.label);

      if (options.json) {
        console.log(JSON.stringify(snapshot, null, 2));
        process.exit(0);
        return;
      }

      console.log();
      console.log(chalk.green("✓") + chalk.bold(" Snapshot created"));
      console.log(`  ID:          ${chalk.cyan(snapshot.id)}`);
      console.log(`  Label:       ${snapshot.label}`);
      console.log(`  RN Version:  ${chalk.cyan(snapshot.reactNativeVersion)}`);
      if (snapshot.expoVersion) {
        console.log(`  Expo:        ${chalk.cyan(snapshot.expoVersion)}`);
      }
      console.log(`  Packages:    ${chalk.cyan(snapshot.dependencies.length)}`);
      console.log(`  Plugins:     ${chalk.cyan(snapshot.loadedPlugins.length)}`);
      console.log(`  Created:     ${snapshot.createdAt}`);
      console.log();
    } catch (err: any) {
      if (options.json) {
        console.log(JSON.stringify({ success: false, error: err?.message }, null, 2));
      } else {
        console.log(chalk.red(`✖ ${err?.message}`));
      }
      process.exit(1);
    }
  });

// ── snapshot list ──────────────────────────────────────────────────────────

const snapshotListCommand = new Command("list")
  .description("List all project snapshots")
  .option("--json", "Output in JSON format")
  .action((options: { json?: boolean }) => {
    const cwd = process.cwd();
    const snapshots = listSnapshots(cwd);

    if (options.json) {
      console.log(JSON.stringify(snapshots, null, 2));
      return;
    }

    console.log();
    if (snapshots.length === 0) {
      console.log(chalk.dim("No snapshots found. Run: kyro snapshot create"));
      console.log();
      return;
    }

    console.log(chalk.bold.underline(`Project Snapshots (${snapshots.length})`));
    console.log();
    for (const s of snapshots) {
      console.log(`  ${chalk.cyan(s.id)}`);
      console.log(`  ${chalk.dim("Label:")}    ${s.label}`);
      console.log(`  ${chalk.dim("Created:")}  ${s.createdAt}`);
      console.log();
    }
  });

// ── snapshot restore ───────────────────────────────────────────────────────

const snapshotRestoreCommand = new Command("restore")
  .description("Restore a snapshot (review package.json content from that snapshot)")
  .argument("<id>", "Snapshot ID to restore")
  .option("--json", "Output in JSON format")
  .option("--write", "Write the snapshotted package.json to disk (requires confirmation)")
  .action(async (id: string, options: { json?: boolean; write?: boolean }) => {
    const cwd = process.cwd();
    try {
      const result = restoreSnapshot(cwd, id);
      if (!result) {
        const msg = `Snapshot "${id}" not found.`;
        if (options.json) {
          console.log(JSON.stringify({ success: false, error: msg }, null, 2));
        } else {
          console.log(chalk.red(`✖ ${msg}`));
        }
        process.exit(1);
        return;
      }

      const { snapshot, packageJsonContent } = result;

      if (options.json) {
        console.log(JSON.stringify({ snapshot, packageJsonContent }, null, 2));
        process.exit(0);
        return;
      }

      console.log();
      console.log(chalk.bold.underline(`Snapshot Restore: ${snapshot.label}`));
      console.log();
      console.log(`  ID:          ${chalk.cyan(snapshot.id)}`);
      console.log(`  Created:     ${snapshot.createdAt}`);
      console.log(`  RN Version:  ${chalk.cyan(snapshot.reactNativeVersion)}`);
      console.log(`  Packages:    ${snapshot.dependencies.length}`);
      console.log();

      if (options.write) {
        const pkgJsonPath = findPackageJson(cwd);
        if (!pkgJsonPath) {
          console.log(chalk.red("✖ Cannot locate package.json to write to."));
          process.exit(1);
          return;
        }

        // Safety: backup current package.json
        const backup = pkgJsonPath + ".backup";
        fs.copyFileSync(pkgJsonPath, backup);
        fs.writeFileSync(pkgJsonPath, packageJsonContent, "utf-8");

        console.log(chalk.green("✓") + chalk.bold(" package.json restored from snapshot"));
        console.log(`  ${chalk.dim("Backup saved to:")} ${backup}`);
        console.log();
        console.log(chalk.yellow("  Next steps:"));
        console.log(`  ${chalk.dim("·")} Run: kyro sync`);
        console.log(`  ${chalk.dim("·")} Run: kyro doctor`);
        console.log();
      } else {
        console.log(chalk.dim("Snapshot package.json preview (first 500 chars):"));
        console.log(chalk.dim(packageJsonContent.slice(0, 500)));
        console.log();
        console.log(chalk.dim("To restore: kyro snapshot restore <id> --write"));
        console.log();
      }
    } catch (err: any) {
      console.log(chalk.red(`✖ ${err?.message}`));
      process.exit(1);
    }
  });

// ── snapshot root command ──────────────────────────────────────────────────

export const snapshotCommand = new Command("snapshot")
  .description("Manage project snapshots (create, list, restore)")
  .addCommand(snapshotCreateCommand)
  .addCommand(snapshotListCommand)
  .addCommand(snapshotRestoreCommand);
